import { useEffect } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { CREDIT_REFRESH_EVENT } from '../../services/api/creditService';
import {
  UnnotifiedInviteReward,
  fetchPendingInviteRewards,
  markInviteRewardsNotified,
} from '../../services/api/inviteService';

const rewardMessage = (reward: UnnotifiedInviteReward): string => {
  const who = reward.invitee_name ? `${reward.invitee_name}さん` : '招待した方';
  const amount = reward.amount.toLocaleString();

  return reward.stage === 'signup'
    ? `${who}が登録しました。${amount}cr を付与しました`
    : `${who}が最初のバナーを作りました。${amount}cr を付与しました`;
};

/**
 * 招待報酬の付与を招待者に知らせる。
 *
 * 付与は被招待者の操作で非同期に起きるため、招待者の画面にリアルタイムでは
 * 出せない。次にホームを開いた時にまとめてトーストし、既読にする
 * （仕様 3-4: 無言で残高だけ変わる状態にしない）。
 */
const useInviteRewardToasts = (): void => {
  const { showToast } = useToast();

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      let pending: UnnotifiedInviteReward[] = [];

      try {
        pending = await fetchPendingInviteRewards();
      } catch {
        // 通知は付随機能。取得に失敗してもホームの表示は妨げない。
        return;
      }

      if (cancelled || pending.length === 0) {
        return;
      }

      pending.forEach((reward) => showToast(rewardMessage(reward), 'success'));

      try {
        await markInviteRewardsNotified(
          pending.map(({ referral_id, stage }) => ({ referral_id, stage })),
        );
      } catch {
        // 既読化に失敗したら次回また通知される。二重付与にはならないので許容する。
      }

      // ヘッダーのクレジット残高を増えた状態に更新する。
      window.dispatchEvent(new Event(CREDIT_REFRESH_EVENT));
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [showToast]);
};

export default useInviteRewardToasts;
